import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SEO from '../components/SEO.tsx';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
  },
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const projects = [
  {
    id: 1,
    title: 'E-commerce Platform',
    description: 'A full-stack e-commerce platform built with Spring Boot and React. Features include user authentication, product management, and payment integration.',
    image: '/projects/ecommerce.svg',
    technologies: ['Java', 'Spring Boot', 'React', 'PostgreSQL'],
    githubUrl: 'https://github.com/lalik77/ecommerce-platform',
    liveUrl: 'https://ecommerce-demo.mamialexandre.fr',
  },
  {
    id: 2,
    title: 'Task Management System',
    description: 'A collaborative task management system with real-time updates and team features. Built using Spring Boot and WebSocket.',
    image: '/projects/task-management.svg',
    technologies: ['Java', 'Spring Boot', 'WebSocket', 'MySQL'],
    githubUrl: 'https://github.com/lalik77/task-management',
    liveUrl: 'https://tasks.mamialexandre.fr',
  },
  {
    id: 3,
    title: 'Weather Dashboard',
    description: 'A responsive weather dashboard that displays real-time weather data and forecasts using external APIs.',
    image: '/projects/weather.svg',
    technologies: ['React', 'TypeScript', 'Material-UI', 'REST API'],
    githubUrl: 'https://github.com/lalik77/weather-dashboard',
    liveUrl: 'https://weather.mamialexandre.fr',
  },
  {
    id: 4,
    title: 'AI-Agent',
    description: 'An intelligent AI-powered agent built with React and JavaScript that demonstrates advanced conversational capabilities and task automation.',
    image: '/projects/ai-agent.svg',
    technologies: ['React', 'JavaScript', 'Material-UI', 'Vite'],
    githubUrl: 'https://github.com/lalik77/portfolio-react-spring',
    liveUrl: 'https://mamialexandre.fr',
  },
  // Add more projects as needed
];

const Portfolio = () => {
  useEffect(() => {
    document.title = 'Alexandre Mami | Portfolio';
  }, []);

  return (
    <>
      <SEO
        title="Portfolio"
        description="Découvrez les projets de développement d'Alexandre Mami : plateformes e-commerce, systèmes de gestion, tableaux de bord météo et solutions Full Stack."
        keywords="portfolio, projets, développement, java, spring, react, full stack, alexandre mami"
        ogImage="https://mamialex.ru/images/portfolio-preview.jpg"
        ogType="portfolio"
      />
      <Box
        sx={{
          minHeight: '100%',
          py: 8,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        }}
      >
        <Container maxWidth="lg">
            <Typography
            variant="h2"
            gutterBottom
            sx={{
              textAlign: 'center',
              mb: 6,
              background: 'linear-gradient(45deg, #6366F1 30%, #FFC107 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}
          >
            My Projects (Under Construction)
          </Typography>

          <Grid container spacing={4}>
            {projects.map((project) => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <StyledCard>
                  <CardMedia
                    component="img"
                    height="200"
                    image={project.image}
                    alt={project.title}
                    onError={(e) => {
                      console.error(`Failed to load image: ${project.image}`, e);
                      e.currentTarget.src = '/favicon.ico'; // fallback
                    }}
                    onLoad={() => console.log(`Successfully loaded image: ${project.image}`)}
                    sx={{
                      objectFit: 'cover',
                      width: '100%',
                      height: '200px',
                      display: 'block',
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h5" component="h2">
                      {project.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {project.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {project.technologies.map((tech) => (
                        <Chip
                          key={tech}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white',
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                  <CardActions sx={{ p: 2 }}>
                    <Button
                      size="small"
                      startIcon={<GitHubIcon />}
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#6366F1',
                        '&:hover': {
                          color: '#FFC107',
                        },
                      }}
                    >
                      Code
                    </Button>
                    <Button
                      size="small"
                      startIcon={<LaunchIcon />}
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#6366F1',
                        '&:hover': {
                          color: '#FFC107',
                        },
                      }}
                    >
                      Demo
                    </Button>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Portfolio; 